import {Component, Input, OnInit,EventEmitter, Output} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { AuthService } from '../../../../services/auth.service';
import { FlagService } from '../../../../shared/services/flag.service';
import { FlagType } from '../../../../shared/utils/enums';
import { Router } from '@angular/router';
import { PASSWORD_RULES } from '../../../../shared/utils/agency-rbac';
import { civilityOptions } from '../../../../shared/utils/data/etablissments-data';

@Component({
  selector: 'app-components',
  templateUrl: './components.component.html',
  styleUrl: './components.component.scss'
})
export class ComponentsComponent implements OnInit {
  @Input() agenceInscriptionFormControl: FormGroup;
  @Output() onSubmit = new EventEmitter<any>();

  step: number = 1;
  loading = false;
  showPassword: boolean;
  readonly passwordRules = PASSWORD_RULES;
  protected readonly civilityOptions = civilityOptions;

  get passwordFormField() {
    return this.agenceInscriptionFormControl.get('password');
  }

  get siretFormField() {
    return this.agenceInscriptionFormControl.get('siret');
  }

  /** The agency contact email (optional) must differ from the dirigeant's login email. */
  get sameEmails(): boolean {
    const agencyEmail = (this.agenceInscriptionFormControl.get('agencyEmail')?.value ?? '').trim().toLowerCase();
    const loginEmail = (this.agenceInscriptionFormControl.get('email')?.value ?? '').trim().toLowerCase();
    return !!agencyEmail && agencyEmail === loginEmail;
  }

  constructor(private authService: AuthService, private flag: FlagService,private router: Router) {

  }

  ngOnInit(): void {
  }

  signupAgency()
  {
    const f = this.agenceInscriptionFormControl;

    // adresseSiege / raisonSociale are disabled and only filled by SIRET validation,
    // so an empty value means the SIRET still needs to be validated.
    if (!f.get('adresseSiege')?.value || !f.get('raisonSociale')?.value) {
      this.flag.displayFlag('Veuillez valider le SIRET de l\'agence.', FlagType.error, 2);
      return;
    }
    if (this.sameEmails) {
      this.flag.displayFlag("L'e-mail de l'agence doit être différent de l'e-mail de connexion du dirigeant.", FlagType.error, 2);
      return;
    }
    if (f.invalid) {
      f.markAllAsTouched();
      this.flag.displayFlag('Veuillez compléter tous les champs requis.', FlagType.error, 2);
      return;
    }

    this.loading = true;
    const trim = (v: any) => (typeof v === 'string' ? v.trim() : v) || null;

    // Maps to the backend OnboardRequest: agency (+ non-login tenant) and the dirigeant principal
    // (AGENCY_USER, role ADMIN) whose email is the only login email.
    const body = {
      agency: {
        name: trim(f.get('nomAgence')?.value),
        siret: trim(f.get('siret')?.value),
        raisonSociale: trim(f.get('raisonSociale')?.value),
        address: trim(f.get('adresseSiege')?.value),
        email: trim(f.get('agencyEmail')?.value),
        phone: trim(f.get('agencyPhone')?.value),
      },
      dirigeant: {
        email: trim(f.get('email')?.value),
        password: f.get('password')?.value,
        firstName: trim(f.get('dirigeantPrenom')?.value),
        lastName: trim(f.get('dirigeantNom')?.value),
        phone: trim(f.get('dirigeantPhone')?.value),
        title: trim(f.get('dirigeantTitle')?.value),
        fonction: trim(f.get('dirigeantFonction')?.value),
      }
    };

    this.authService.onboard(body).subscribe({
      next: (res) => {
        this.loading = false;
        if (res && res.status === 'success') {
          this.flag.displayFlag(
            "Agence créée avec succès. Connectez-vous avec l'e-mail du dirigeant : un code de vérification vous sera envoyé.",
            FlagType.success, 4);
          this.router.navigate(['/login']);
        }
      },
      error: (e) => {
        this.loading = false;
        const msg = e?.error?.message
          || (e?.status === 409
                ? 'Cette instance a déjà été configurée.'
                : 'Une erreur est survenue lors de la création de l\'agence.');
        this.flag.displayFlag(msg, FlagType.error, 3);
      }
    });
  }

  validateSiret() {
    this.authService.validateSiret(this.agenceInscriptionFormControl.get('siret')?.value).subscribe({
      next: (res) => {
        // scanfacture.fr identity payload: denomination + adresse/code_postal/ville flat fields.
        const raisonSociale = res?.denomination;
        const fullAddress = [res?.adresse, res?.code_postal, res?.ville]
          .filter((part: string) => !!part)
          .join(' ')
          .trim();

        if (!raisonSociale && !fullAddress) {
          this.flag.displayFlag('Le SIRET n\'a pas été trouvé dans l\'API.', FlagType.error, 2);
          this.agenceInscriptionFormControl.get('raisonSociale')?.patchValue('');
          this.agenceInscriptionFormControl.get('adresseSiege')?.patchValue('');
          return;
        }
        if (res?.etat_administratif && res.etat_administratif !== 'A') {
          this.flag.displayFlag('Ce SIRET correspond à un établissement administrativement fermé.', FlagType.error, 2);
        }

        this.agenceInscriptionFormControl.get('raisonSociale')?.patchValue(raisonSociale || '');
        this.agenceInscriptionFormControl.get('adresseSiege')?.patchValue(fullAddress);
      },
      error: () => {
        this.flag.displayFlag('Le SIRET n\'a pas été trouvé dans l\'API.', FlagType.error, 2);
        //clear the inputs of raison sociale and adresseSiege if the siret is invalid
        this.agenceInscriptionFormControl.get('raisonSociale')?.patchValue('');
        this.agenceInscriptionFormControl.get('adresseSiege')?.patchValue('');
      }
    })
  }

}
